import qs from 'query-string'

import type {
  TransportRequestConfig,
  TransportResponse,
} from '@mangovoice/api-client'
import type { PaginatedApiResult } from '@mangovoice/api-client'
import type { MangoApiClient } from '@mangovoice/mango-api-client'
import type { ExtensionInstance } from '@mangovoice/types'

import { SWITCHBOARD_ENDPOINTS } from './endpoints'
import type {
  ApiError,
  CreateSwitchboardRequest,
  ListExtensionsParams,
  ListMessageGroupsParams,
  ListPhoneNumbersParams,
  ListSchedulesParams,
  ListSoundClipsParams,
  ListSwitchboardsParams,
  ListSwitchboardsResponse,
  MessageGroup,
  Pbx,
  PhoneNumber,
  Schedule,
  SoundClip,
  Switchboard,
  UpdatePbxRequest,
  UpdateSwitchboardRequest,
} from './types'

/**
 * Normalize PBX response: convert boolean day_night fields to strings
 * Backend returns booleans (true/false), but frontend expects strings ('0'/'1')
 * to match legacy format and TypeScript types.
 */
const normalizePbxResponse = (pbx: Pbx): Pbx => {
  const normalized: Pbx = { ...pbx }

  // Normalize day_night field (Advanced mode)
  if (typeof pbx.day_night === 'boolean') {
    normalized.day_night = pbx.day_night ? '1' : '0'
  }

  // Normalize day_night_0 through day_night_9 (Basic mode)
  for (let i = 0; i <= 9; i++) {
    const field = `day_night_${i}` as keyof Pbx
    const value = pbx[field]
    if (typeof value === 'boolean') {
      ;(normalized as Record<string, '0' | '1'>)[field] = value ? '1' : '0'
    }
  }

  return normalized
}

/**
 * Create switchboard API functions from a client
 */
export const createSwitchboardApi = (
  client: MangoApiClient['switchboard'],
) => ({
  /**
   * List all switchboards for a PBX
   * @param params - Query parameters including required pbx_id
   * @returns Promise with switchboards list and pagination metadata
   */
  listSwitchboards: async (
    params: ListSwitchboardsParams,
  ): Promise<TransportResponse<ListSwitchboardsResponse>> => {
    const queryString = qs.stringify(params, {
      skipNull: true,
      skipEmptyString: true,
    })
    const url = queryString
      ? `${SWITCHBOARD_ENDPOINTS.LIST}?${queryString}`
      : SWITCHBOARD_ENDPOINTS.LIST

    return client.get<ListSwitchboardsResponse>(url)
  },

  /**
   * Get a single switchboard by extension_dial_plan
   * @param extensionDialPlan - The extension dial plan ID
   * @returns Promise with switchboard data
   */
  getSwitchboard: async (
    extensionDialPlan: number,
  ): Promise<TransportResponse<Switchboard>> => {
    return client.get<Switchboard>(
      SWITCHBOARD_ENDPOINTS.DETAIL(extensionDialPlan),
    )
  },

  /**
   * Create a new switchboard
   * @param data - Switchboard creation data
   * @returns Promise with created switchboard
   */
  createSwitchboard: async (
    data: CreateSwitchboardRequest,
  ): Promise<TransportResponse<Switchboard>> => {
    return client.post<Switchboard>(SWITCHBOARD_ENDPOINTS.CREATE, data)
  },

  /**
   * Update an existing switchboard
   * @param extensionDialPlan - The extension dial plan ID (used in URL path)
   * @param data - Update data (json_data, name, number, omit_from_directory)
   * @returns Promise with updated switchboard
   * @note Uses PATCH method as per API specification
   * @note Only fields that are explicitly provided (not undefined) will be included in the request.
   *       This allows partial updates - e.g., updating only name/number without changing json_data.
   */
  updateSwitchboard: async (
    extensionDialPlan: number,
    data: UpdateSwitchboardRequest,
  ): Promise<TransportResponse<Switchboard>> => {
    // Only include fields that are explicitly provided (not undefined)
    const body: Record<string, unknown> = {}
    if (data.json_data !== undefined) {
      body.json_data = data.json_data
    }
    if (data.name !== undefined) {
      body.name = data.name
    }
    if (data.number !== undefined) {
      body.number = data.number
    }
    if (data.omit_from_directory !== undefined) {
      body.omit_from_directory = data.omit_from_directory
    }

    return client.patch<Switchboard>(
      SWITCHBOARD_ENDPOINTS.UPDATE(extensionDialPlan),
      body,
    )
  },

  /**
   * Delete a switchboard
   * @param extensionDialPlan - The extension dial plan ID
   * @returns Promise with delete response
   */
  deleteSwitchboard: async (
    extensionDialPlan: number,
  ): Promise<TransportResponse<undefined>> => {
    return client.delete<undefined>(
      SWITCHBOARD_ENDPOINTS.DELETE(extensionDialPlan),
    )
  },

  /**
   * Get a single PBX by ID
   * @param pbxId - The PBX ID
   * @returns Promise with PBX data including day/night settings
   * @note Normalizes boolean day_night fields to strings ('0'/'1') to match frontend types
   */
  getPbx: async (pbxId: number): Promise<TransportResponse<Pbx>> => {
    const response = await client.get<Pbx>(
      SWITCHBOARD_ENDPOINTS.PBX_DETAIL(pbxId),
    )

    // Normalize response if successful (convert booleans to strings)
    if (response.ok && response.data) {
      return {
        ...response,
        data: normalizePbxResponse(response.data) as Pbx,
      }
    }

    return response
  },

  /**
   * Update PBX day/night settings
   * @param pbxId - The PBX ID (used in URL path, supports both numeric ID and UUID)
   * @param data - Update data (only day_night fields: '0' | '1' strings)
   * @returns Promise with updated PBX
   * @note Only day_night and day_night_0 through day_night_9 fields can be updated
   * @note Endpoint: PATCH /app/v2/pbxes/{id}/ or PATCH /app/v2/pbxes/{uuid}/
   * @note Uses PbxUpdateSerializer which only accepts day_night fields
   * @note All other fields (name, billing_info, etc.) are ignored in updates
   */
  updatePbx: async (
    pbxId: number,
    data: UpdatePbxRequest,
  ): Promise<TransportResponse<Pbx>> => {
    const url = SWITCHBOARD_ENDPOINTS.PBX_UPDATE(pbxId)

    const response = await client.patch<Pbx>(url, data)

    // Normalize response if successful (convert booleans to strings)
    if (response.ok && response.data) {
      return {
        ...response,
        data: normalizePbxResponse(response.data) as Pbx,
      }
    }

    return response
  },

  /**
   * List extensions for a PBX
   * @param params - Query parameters including pbx_id and optional type filter
   * @returns Promise with extensions list
   */
  listExtensions: async (
    params: ListExtensionsParams,
    config?: TransportRequestConfig,
  ): Promise<TransportResponse<PaginatedApiResult<ExtensionInstance>>> => {
    return client.listExtensions(params, config)
  },

  /**
   * List message groups for a PBX
   * @param params - Query parameters including pbx_id
   * @returns Promise with message groups list
   */
  listMessageGroups: async (
    params: ListMessageGroupsParams,
  ): Promise<TransportResponse<PaginatedApiResult<MessageGroup>>> => {
    const queryString = qs.stringify(params, {
      skipNull: true,
      skipEmptyString: true,
    })
    const url = queryString
      ? `${SWITCHBOARD_ENDPOINTS.MESSAGE_GROUPS_LIST}?${queryString}`
      : SWITCHBOARD_ENDPOINTS.MESSAGE_GROUPS_LIST

    return client.get<PaginatedApiResult<MessageGroup>>(url)
  },

  /**
   * List sound clips for a PBX
   * @param params - Query parameters including optional pbx_id
   * @returns Promise with sound clips list
   */
  listSoundClips: async (
    params: ListSoundClipsParams,
  ): Promise<TransportResponse<PaginatedApiResult<SoundClip>>> => {
    const queryString = qs.stringify(params, {
      skipNull: true,
      skipEmptyString: true,
    })
    const url = queryString
      ? `${SWITCHBOARD_ENDPOINTS.SOUND_CLIPS_LIST}?${queryString}`
      : SWITCHBOARD_ENDPOINTS.SOUND_CLIPS_LIST

    return client.get<PaginatedApiResult<SoundClip>>(url)
  },

  /**
   * Get a single sound clip by ID
   * @param id - Sound clip ID
   * @returns Promise with sound clip data
   */
  getSoundClip: async (id: number): Promise<TransportResponse<SoundClip>> => {
    return client.get<SoundClip>(SWITCHBOARD_ENDPOINTS.SOUND_CLIP_DETAIL(id))
  },

  /**
   * List schedules for a PBX
   * @param params - Query parameters including pbx_id
   * @returns Promise with schedules list
   */
  listSchedules: async (
    params: ListSchedulesParams,
  ): Promise<TransportResponse<PaginatedApiResult<Schedule>>> => {
    const queryString = qs.stringify(params, {
      skipNull: true,
      skipEmptyString: true,
    })
    const url = queryString
      ? `${SWITCHBOARD_ENDPOINTS.SCHEDULES_LIST}?${queryString}`
      : SWITCHBOARD_ENDPOINTS.SCHEDULES_LIST

    return client.get<PaginatedApiResult<Schedule>>(url)
  },

  /**
   * List phone numbers for a PBX
   * @param params - Query parameters including pbx_id
   * @returns Promise with phone numbers list
   */
  listPhoneNumbers: async (
    params: ListPhoneNumbersParams,
  ): Promise<TransportResponse<PaginatedApiResult<PhoneNumber>>> => {
    const queryString = qs.stringify(params, {
      skipNull: true,
      skipEmptyString: true,
    })
    const url = queryString
      ? `${SWITCHBOARD_ENDPOINTS.PHONE_NUMBERS_LIST}?${queryString}`
      : SWITCHBOARD_ENDPOINTS.PHONE_NUMBERS_LIST

    return client.get<PaginatedApiResult<PhoneNumber>>(url)
  },
})

export type SwitchboardApi = ReturnType<typeof createSwitchboardApi>
